import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AuthHeader } from "services/authHeader";
import { ActivityEmailSliceModel, EmailConversationRequest, EmailListResponse, NextPageLinkObject } from "./activity-email-interface";
import { ErrorResponse, Message, RejectedValue } from "shared/interfaces/core-interfaces";
import { API_BASE_URL as baseUri } from "config/env";

export const GetEmailById = createAsyncThunk<Message[], EmailConversationRequest, { rejectValue: RejectedValue }>(
    'Slice/ActivityEmailSlice/GetEmailById',
    async (emailConversationRequest: EmailConversationRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/emails/get-by-thread`, {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(emailConversationRequest)
        });

        if (response.status === 200) {
            const responseModel = await response.json() as Message[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetMeetingById = createAsyncThunk<Message[], { eventId: string, creatorId: string }, { rejectValue: RejectedValue }>(
    'Slice/ActivityEmailSlice/GetMeetingById',
    async (event: { eventId: string, creatorId: string }, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/emails/get-meeting`, {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(event)
        });

        if (response.status === 200) {
            const responseModel = await response.json() as Message[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetEmailAttachment = createAsyncThunk<Message[], EmailConversationRequest, { rejectValue: RejectedValue }>(
    'Slice/ActivityEmailSlice/GetEmailAttachment',
    async (emailConversationRequest: EmailConversationRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/emails/get-by-thread`, {
            method: "GET",
            headers: AuthHeader(),
            body: JSON.stringify(emailConversationRequest)
        });

        if (response.status === 200) {
            const responseModel = await response.json() as Message[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetInboxByEmployee = createAsyncThunk<EmailListResponse, NextPageLinkObject, { rejectValue: RejectedValue }>(
    'Slice/ActivityEmailSlice/GetInboxByEmployee',
    async (nextPageLinkObject, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/emails/get-by-employee`, {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(nextPageLinkObject)
        });

        if (response.status === 200) {
            const responseModel = await response.json() as EmailListResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetSentByEmployee = createAsyncThunk<EmailListResponse, NextPageLinkObject, { rejectValue: RejectedValue }>(
    'Slice/ActivityEmailSlice/GetSentByEmployee',
    async (nextPageLinkObject, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/emails/get-sent-by-employee`, {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(nextPageLinkObject)
        });

        if (response.status === 200) {
            const responseModel = await response.json() as EmailListResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

const initialValue: ActivityEmailSliceModel = {
    emailMessage: null,
    emailInboxResponse: null,
    emailSentResponse: null,
    meeting: null,
    statusCode: "",
    message: "",
    showNotification: false,
    reloadData: false,

    isLoading: false
}

export const ActivityEmailSlice = createSlice({
    name: 'ActivityEmailSlice',
    initialState: initialValue,
    reducers: {
        resetEmailSlice: (state) => {
            state = initialValue
        },
        resetStatusCode: (state) => {
            state.statusCode = '';
            state.message = '';
            state.showNotification = false;
        },
    },
    extraReducers(builder) {
        builder.addCase(GetEmailById.fulfilled, (state, action: PayloadAction<Message[]>) => {
            state.isLoading = false;
            state.statusCode = "200";
            state.emailMessage = action.payload
        })
            .addCase(GetEmailById.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetEmailById.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(GetMeetingById.fulfilled, (state, action: PayloadAction<Message[]>) => {
            state.isLoading = false;
            state.statusCode = "200";
            state.meeting = action.payload
        })
            .addCase(GetMeetingById.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetMeetingById.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(GetInboxByEmployee.fulfilled, (state, action: PayloadAction<EmailListResponse>) => {
            state.isLoading = false;
            state.statusCode = "200";
            state.emailInboxResponse = action.payload
        })
            .addCase(GetInboxByEmployee.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetInboxByEmployee.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(GetSentByEmployee.fulfilled, (state, action: PayloadAction<EmailListResponse>) => {
            state.isLoading = false;
            state.statusCode = "200";
            state.emailSentResponse = action.payload
        })
            .addCase(GetSentByEmployee.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetSentByEmployee.pending, state => {
                state.isLoading = true;
            })
    }
});

export const { resetEmailSlice, resetStatusCode } = ActivityEmailSlice.actions;
export default ActivityEmailSlice.reducer;